import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Crown, Zap, Sparkles, ArrowRight, Settings2, Clock, AlertCircle, Trash2, Loader2, X, CreditCard } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBilling } from "../lib/billing";
import { toast } from "sonner";
import { fmtMoney } from "../lib/format";

const TIERS = [
  {
    key: "free",
    name: "Free",
    icon: Sparkles,
    monthly: 0,
    yearly: 0,
    tagline: "Get a feel for the booth.",
    features: [
      "20 products",
      "1 active event",
      "Solo (no team)",
      "Webcam barcode scan",
      "Print barcode tags",
      "By-day sales report",
      "Offline mode",
    ],
    locked: ["CSV import", "Per-seller payouts", "COGS & margin", "Hierarchical categories"],
  },
  {
    key: "pro",
    name: "Pro",
    icon: Zap,
    monthly: 15,
    yearly: 144,
    tagline: "For working show vendors.",
    highlighted: true,
    features: [
      "Unlimited products",
      "Unlimited events",
      "Up to 5 team members",
      "Per-seller payouts",
      "COGS & margin tracking",
      "Hierarchical categories",
      "CSV import / export",
      "No Vendor Vault badge",
    ],
  },
  {
    key: "business",
    name: "Business",
    icon: Crown,
    monthly: 39,
    yearly: 374,
    tagline: "Multi-booth crews.",
    features: [
      "Everything in Pro",
      "Unlimited team members",
      "Priority support",
      "Multi-store support (coming soon)",
      "API access (coming soon)",
    ],
  },
];

function fmtPrice(n) {
  if (n === 0) return "$0";
  return fmtMoney(n);
}

export default function Billing() {
  const { billing, refresh } = useBilling();
  const { user, logout } = useAuth();
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const canceled = searchParams.get("canceled");
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Poll checkout status after Stripe redirect
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}/status`);
        if (data.payment_status === "paid" || data.status === "complete") {
          toast.success("Subscription active! Welcome to the team.");
          await refresh();
          if (!cancelled) {
            // remove session_id from URL
            searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
          }
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          return;
        }
        if (attempts < 6 && !cancelled) {
          attempts += 1;
          setTimeout(poll, 2000);
        }
      } catch (_) { /* ignore */ }
    };
    toast.info("Confirming your subscription…");
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (canceled) {
      toast.message("Checkout canceled — you weren't charged.");
      searchParams.delete("canceled");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canceled]);

  const upgraded = searchParams.get("upgraded");
  useEffect(() => {
    if (upgraded) {
      // refresh in case the redirect came from a hard navigation
      refresh();
      searchParams.delete("upgraded");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgraded]);

  const upgradeTo = async (tierKey) => {
    if (tierKey === "free") return;
    setBusy(tierKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier: tierKey,
        interval,
        origin_url: window.location.origin,
      });
      if (data?.applied) {
        // In-place plan swap on existing subscription — no Checkout, no payment input needed.
        toast.success(`Plan switched to ${tierKey === "business" ? "Business" : "Pro"}!`);
        await refresh();
        if (data?.redirect) {
          // Clean URL marker; no full redirect needed since we're already on /billing.
          searchParams.set("upgraded", "1");
          setSearchParams(searchParams, { replace: true });
        }
      } else if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/billing`,
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal.");
    } finally {
      setBusy(null);
    }
  };

  const currentTier = billing?.tier || "free";
  const status = billing?.status || "free";
  const trialingEnd = useMemo(() => {
    if (!billing?.trial_end_at) return null;
    return new Date(billing.trial_end_at);
  }, [billing]);
  const trialDaysLeft = useMemo(() => {
    if (!trialingEnd) return null;
    const ms = trialingEnd - new Date();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }, [trialingEnd]);

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Pricing</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Plans &amp; Billing</h1>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="brutal-btn brutal-btn-secondary"
          data-testid="billing-back-btn"
        >
          ← Back
        </button>
      </div>

      {/* Current plan callout */}
      {billing && (
        <div className="brutal-card p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="current-plan-card">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">Current plan</div>
            <div className="font-heading text-2xl font-black uppercase mt-1">
              {billing.tier_name} <span className="brutal-badge ml-2 align-middle">{status}</span>
            </div>
            {status === "trialing" && trialDaysLeft !== null && (
              <div className="font-mono text-xs mt-2 inline-flex items-center gap-2 text-[#4A4A4A]">
                <Clock size={12} /> Trial: {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left · ends {trialingEnd?.toLocaleDateString()}
              </div>
            )}
            {billing.cancel_at_period_end && billing.current_period_end && (
              <div className="font-mono text-xs mt-2 inline-flex items-center gap-2 text-[#C2410C]">
                <AlertCircle size={12} /> Cancels on {new Date(billing.current_period_end).toLocaleDateString()}
              </div>
            )}
            {billing.usage && (
              <div className="font-mono text-xs mt-3 text-[#4A4A4A]">
                Usage — Products: {billing.usage.products}{billing.limits.max_products ? ` / ${billing.limits.max_products}` : ""}
                {" · "}Events: {billing.usage.events}{billing.limits.max_events ? ` / ${billing.limits.max_events}` : ""}
                {" · "}Team: {billing.usage.team_members}{billing.limits.max_team_members ? ` / ${billing.limits.max_team_members}` : ""}
              </div>
            )}
          </div>
          {billing.has_stripe_customer && (
            <button
              onClick={openPortal}
              disabled={busy === "portal"}
              className="brutal-btn brutal-btn-secondary"
              data-testid="manage-subscription-btn"
            >
              <Settings2 size={14} className="mr-2"/>
              {busy === "portal" ? "Opening…" : "Manage subscription"}
            </button>
          )}
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex items-center justify-center gap-3" data-testid="interval-toggle">
        <button
          onClick={() => setInterval("month")}
          className={`brutal-btn ${interval === "month" ? "" : "brutal-btn-secondary"}`}
          data-testid="interval-month"
        >
          Monthly
        </button>
        <button
          onClick={() => setInterval("year")}
          className={`brutal-btn ${interval === "year" ? "" : "brutal-btn-secondary"}`}
          data-testid="interval-year"
        >
          Yearly <span className="ml-2 brutal-badge !bg-[#FFB100] !py-0">SAVE 20%</span>
        </button>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const isCurrent = currentTier === t.key;
          const price = interval === "month" ? t.monthly : t.yearly;
          const priceSuffix = t.key === "free" ? "" : (interval === "month" ? "/mo" : "/yr");
          return (
            <div
              key={t.key}
              className={`brutal-card p-6 flex flex-col gap-4 ${t.highlighted ? "!bg-[#111] !text-white corner-marks" : ""}`}
              data-testid={`tier-card-${t.key}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 border-2 ${t.highlighted ? "border-white" : "border-black"} flex items-center justify-center`}>
                  <Icon size={18}/>
                </div>
                <div>
                  <div className="font-heading text-2xl font-black uppercase">{t.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">{t.tagline}</div>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-5xl font-black">{fmtPrice(price)}</div>
                <div className="font-mono text-xs opacity-80">{priceSuffix}</div>
              </div>
              <ul className="space-y-2">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-mono text-xs">
                    <Check size={14} className="mt-0.5 flex-shrink-0"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-2">
                {isCurrent ? (
                  <button disabled className="brutal-btn w-full !cursor-default opacity-80" data-testid={`tier-cta-${t.key}-current`}>
                    Current plan
                  </button>
                ) : t.key === "free" ? (
                  <button
                    onClick={openPortal}
                    disabled={busy === "portal" || !billing?.has_stripe_customer}
                    className="brutal-btn brutal-btn-secondary w-full disabled:opacity-50"
                    data-testid="tier-cta-free-downgrade"
                  >
                    {billing?.has_stripe_customer ? "Downgrade" : "Free by default"}
                  </button>
                ) : (
                  <button
                    onClick={() => upgradeTo(t.key)}
                    disabled={busy === t.key}
                    className={`brutal-btn w-full ${t.highlighted ? "!bg-[#C2410C] !border-white" : ""}`}
                    data-testid={`tier-cta-${t.key}`}
                  >
                    {busy === t.key ? "Redirecting…" : (currentTier === "business" && t.key === "pro" ? "Switch to Pro" : "Upgrade")}
                    <ArrowRight size={14} className="ml-2 inline"/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="font-mono text-xs text-[#4A4A4A] text-center">
        Yearly plans billed once per year. Cancel anytime from the billing portal — you keep access until the end of the period.
      </div>

      <ShopifyIntegrationCard />

      <SquareIntegrationCard />

      {/* Danger zone — self-serve account deletion */}
      <div className="brutal-card !shadow-none border-2 border-[#C53030] p-5 space-y-3" data-testid="danger-zone">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#C53030]">Danger Zone</div>
          <h3 className="font-heading text-xl font-black uppercase mt-1">Delete my account</h3>
        </div>
        <p className="font-mono text-sm text-[#4A4A4A] max-w-2xl">
          Permanently deletes your account, all your products, events, sales, and (if you're a shop admin) every teammate's data too. Active subscriptions are not auto-cancelled — visit <span className="underline">Manage subscription</span> first if you have one running. This cannot be undone.
        </p>
        <button
          onClick={() => setDeleteOpen(true)}
          className="brutal-btn !bg-white !text-[#C53030] !border-[#C53030]"
          data-testid="self-delete-open-btn"
        >
          <Trash2 size={14} className="inline mr-2"/> Delete my account
        </button>
      </div>

      <SelfDeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        userEmail={user?.email}
        onDeleted={async () => { await logout(); navigate("/", { replace: true }); }}
      />
    </div>
  );
}

// =====================================================================
// Self-delete modal
// =====================================================================
function SelfDeleteAccountModal({ open, onClose, userEmail, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setConfirm(""); setPassword(""); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (confirm !== "DELETE") { toast.error('Type DELETE to confirm.'); return; }
    setBusy(true);
    try {
      await api.delete("/auth/me/account", { data: { confirm, password } });
      toast.success("Account deleted.");
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete account");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="self-delete-modal">
      <div className="brutal-card !bg-white w-full max-w-md p-6 space-y-4" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#C53030] flex items-center gap-1"><AlertCircle size={14}/> Permanent</div>
            <h3 className="font-heading text-2xl font-black mt-1 uppercase">Delete account</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F2EC]"><X size={18}/></button>
        </div>
        <div className="font-mono text-sm">
          You are about to permanently delete <span className="font-bold">{userEmail}</span>. All your inventory, sales, events, and teammates will be erased.
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">Type DELETE to confirm</label>
          <input
            type="text"
            value={confirm}
            onChange={(e)=>setConfirm(e.target.value)}
            className="brutal-input w-full"
            placeholder="DELETE"
            autoFocus
            data-testid="self-delete-confirm-input"
          />
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e)=>setPassword(e.target.value)}
            className="brutal-input w-full"
            placeholder="Required if you signed up with email/password"
            data-testid="self-delete-password-input"
          />
          <div className="font-mono text-[10px] text-[#4A4A4A] mt-1">
            Google-only accounts can leave this blank.
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="brutal-btn brutal-btn-secondary flex-1" disabled={busy}>Keep my account</button>
          <button
            onClick={submit}
            className="brutal-btn flex-1 !bg-[#C53030] !text-white !border-black"
            disabled={busy || confirm !== "DELETE"}
            data-testid="self-delete-submit"
          >
            {busy ? <><Loader2 size={14} className="inline mr-1 animate-spin"/> Deleting…</> : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Shopify Integration — one-way push (admin only)
// =====================================================================
function ShopifyIntegrationCard() {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [storeDomain, setStoreDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [locations, setLocations] = useState([]);
  const { billing } = useBilling();
  const integrationsAllowed = !!billing?.limits?.integrations;
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/shopify/settings");
      setSettings(data);
    } catch {
      setSettings({ store_domain: "", has_token: false });
    }
  };
  useEffect(() => { load(); }, []);

  const fetchLocations = async () => {
    try {
      const { data } = await api.post("/shopify/test");
      setLocations(data?.locations || []);
      return data || { ok: false, error: "empty response" };
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "network error";
      return { ok: false, error: msg };
    }
  };
  // Auto-fetch locations once installed
  useEffect(() => {
    if (settings?.has_token && settings?.store_domain) fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.has_token, settings?.store_domain]);

  // Load last_sync_summary from server-side so the diagnostic panel persists
  // across page reloads — useful when users miss the toast and refresh.
  useEffect(() => {
    if (settings?.last_sync_summary && Object.keys(settings.last_sync_summary).length) {
      setLastResult(settings.last_sync_summary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.last_sync_at]);

  if (!settings) return null;

  // Free-tier paywall — show a CTA to upgrade.
  if (!integrationsAllowed) {
    return (
      <div className="brutal-card p-5 space-y-3" data-testid="shopify-card-locked">
        <div className="flex items-start gap-2">
          <Settings2 size={18}/>
          <div className="flex-1">
            <div className="font-mono text-xs uppercase tracking-widest">Integration</div>
            <h3 className="font-heading text-xl font-black uppercase">Shopify · Push Products</h3>
            <p className="font-mono text-xs text-[#4A4A4A] mt-1 max-w-xl">
              Push your booth inventory straight to your Shopify store. Variants, stock, and categories
              sync in one click — your online storefront stays in lockstep with what's on the table.
            </p>
          </div>
          <span className="brutal-badge !bg-[#FFB100]">Pro</span>
        </div>
        <div className="bg-[#F4F2EC] border-2 border-black p-4 font-mono text-xs flex items-center gap-3">
          <Crown size={16}/>
          <span className="flex-1">Available on Pro & Business plans.</span>
          <button
            onClick={()=>navigate("/billing#plans")}
            className="brutal-btn !py-1 !px-3 text-xs !bg-black !text-white"
            data-testid="shopify-upgrade-btn"
          >
            View plans <ArrowRight size={12} className="inline ml-1"/>
          </button>
        </div>
      </div>
    );
  }

  const isConfigured = !!settings.has_token;
  const isGlobalApp = !!settings.has_global_app;
  // In global-app mode, the only thing a merchant needs to supply is the
  // store domain — Client ID & Secret are managed centrally via env vars.
  const credsSaved = isGlobalApp
    ? !!settings.store_domain
    : !!(settings.store_domain && settings.has_client_id && settings.has_client_secret);
  const hasLocation = !!settings.default_location_id;

  const saveCreds = async (e) => {
    e?.preventDefault();
    if (!storeDomain.trim()) {
      toast.error("Store domain is required");
      return;
    }
    if (!isGlobalApp && (!clientId.trim() || !clientSecret.trim())) {
      toast.error("All three fields are required");
      return;
    }
    setBusy("save");
    try {
      const payload = { store_domain: storeDomain.trim() };
      if (!isGlobalApp) {
        payload.client_id = clientId.trim();
        payload.client_secret = clientSecret.trim();
      }
      await api.put("/shopify/settings", payload);
      toast.success(isGlobalApp
        ? "Store domain saved — now click 'Connect Shopify' to install"
        : "Credentials saved — now click 'Connect Shopify' to install");
      setEditing(false);
      setClientSecret("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally { setBusy(null); }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/shopify/oauth/start");
      // Open Shopify's authorize page so the merchant can install the app.
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start OAuth");
      setBusy(null);
    }
  };

  const setLocation = async (locId) => {
    setBusy("location");
    try {
      await api.put("/shopify/settings", { default_location_id: locId });
      await load();
      toast.success("Location updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update location");
    } finally { setBusy(null); }
  };

  const runSync = async (limit, { skipSynced = false } = {}) => {
    setBusy(skipSynced ? "sync-resume" : (limit ? "sync-test" : "sync"));
    setLastResult(null);
    try {
      const params = new URLSearchParams();
      if (limit) params.set("limit", String(limit));
      if (skipSynced) params.set("skip_synced", "true");
      const qs = params.toString();
      const url = qs ? `/shopify/sync?${qs}` : "/shopify/sync";
      const { data } = await api.post(url);
      // Background-task response: start polling.
      if (data?.background) {
        toast("Sync started in the background. Live progress below…", { duration: 4000 });
        // Poll every 3s until status==done.
        // Cap polls at 10 minutes (200 × 3s) so we don't spin forever.
        for (let i = 0; i < 200; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data: s } = await api.get("/shopify/settings");
            const sum = s?.last_sync_summary || {};
            setLastResult(sum);
            if (sum.status === "done") {
              if ((sum.errors || []).length) {
                toast.error(`Sync finished with ${sum.errors.length} errors — check details below`);
              } else {
                toast.success(`Synced ${sum.products_created + sum.products_updated} products · ${sum.inventory_levels_set} stock levels`);
              }
              await load();
              break;
            }
            if (sum.status === "interrupted") {
              toast.error("Sync was interrupted (worker recycled). Click Resume sync to finish the rest.", { duration: 8000 });
              await load();
              break;
            }
          } catch { /* keep polling */ }
        }
        return;
      }
      // Inline (small) sync result.
      setLastResult(data);
      if (data.dry_run) {
        toast(`Dry-run · ${data.products_seen} products would be pushed`, { duration: 5000 });
      } else if ((data.errors || []).length) {
        toast.error(`Synced with ${data.errors.length} errors — check details below`);
      } else {
        toast.success(`Synced ${data.products_created + data.products_updated} products · ${data.inventory_levels_set} stock levels`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="brutal-card p-5 space-y-3" data-testid="shopify-card">
      <div className="flex items-start gap-2">
        <Settings2 size={18}/>
        <div className="flex-1">
          <div className="font-mono text-xs uppercase tracking-widest">Integration</div>
          <h3 className="font-heading text-xl font-black uppercase">Shopify · Push Products</h3>
          <p className="font-mono text-xs text-[#4A4A4A] mt-1 max-w-xl">
            Push your booth inventory to your Shopify store via OAuth. One-way: Vendor Vault → Shopify.
          </p>
        </div>
        <span className={`brutal-badge ${isConfigured ? "!bg-[#4A7C59] !text-white" : credsSaved ? "!bg-[#FFB100]" : "!bg-[#F4F2EC]"}`}>
          {isConfigured ? "Installed" : credsSaved ? "Pending install" : "Not configured"}
        </span>
      </div>

      {!editing && (settings.store_domain || credsSaved) && (
        <div className="font-mono text-xs text-[#4A4A4A] space-y-1">
          {settings.store_domain && <div>Store: <span className="font-bold text-black">{settings.store_domain}</span></div>}
          {locations.length > 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span>Location:</span>
              <select
                value={settings.default_location_id}
                onChange={(e)=>setLocation(e.target.value)}
                disabled={busy === "location"}
                className="brutal-input !py-1 !px-2 text-xs !w-auto"
                data-testid="shopify-location-select"
              >
                <option value="">— Pick a location —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}{l.primary ? " (primary)" : ""}</option>
                ))}
              </select>
              {!hasLocation && <span className="text-[#C53030]">Required for stock sync</span>}
            </div>
          ) : null}
          {settings.last_sync_at && <div>Last sync {new Date(settings.last_sync_at).toLocaleString()}</div>}
        </div>
      )}

      {editing ? (
        <form onSubmit={saveCreds} className="space-y-3">
          <div>
            <label className="brutal-label">Store domain</label>
            <input
              value={storeDomain}
              onChange={(e)=>setStoreDomain(e.target.value)}
              placeholder="your-store.myshopify.com"
              className="brutal-input w-full"
              data-testid="shopify-domain-input"
            />
            {isGlobalApp && (
              <div className="font-mono text-[10px] text-[#4A4A4A] mt-1">
                Enter your <span className="font-bold">.myshopify.com</span> domain. After saving, click <span className="font-bold">Connect Shopify</span> to authorize — no developer dashboard needed.
              </div>
            )}
          </div>
          {!isGlobalApp && (
            <>
              <div>
                <label className="brutal-label">Client ID</label>
                <input
                  value={clientId}
                  onChange={(e)=>setClientId(e.target.value)}
                  placeholder="from Dev Dashboard → Settings → Credentials"
                  className="brutal-input w-full"
                  data-testid="shopify-client-id-input"
                />
              </div>
              <div>
                <label className="brutal-label">Client Secret</label>
                <input
                  type="password"
                  value={clientSecret}
                  onChange={(e)=>setClientSecret(e.target.value)}
                  placeholder="shpss_..."
                  className="brutal-input w-full"
                  data-testid="shopify-client-secret-input"
                />
                <div className="font-mono text-[10px] text-[#4A4A4A] mt-1">
                  From Dev Dashboard → Vendor Vault → Settings → Credentials (Secret field, click reveal).
                </div>
              </div>
            </>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={()=>{ setEditing(false); setClientSecret(""); }} className="brutal-btn brutal-btn-secondary flex-1">Cancel</button>
            <button type="submit" className="brutal-btn flex-1" disabled={busy === "save"} data-testid="shopify-save-btn">
              {busy === "save" ? "Saving…" : (isGlobalApp ? "Save store" : "Save credentials")}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button onClick={()=>setEditing(true)} className="brutal-btn brutal-btn-secondary" data-testid="shopify-configure-btn">
            {credsSaved ? (isGlobalApp ? "Change store" : "Update credentials") : (isGlobalApp ? "Add store" : "Configure")}
          </button>
          {credsSaved && (
            <button onClick={connect} className="brutal-btn" disabled={busy === "connect"} data-testid="shopify-connect-btn">
              {busy === "connect" ? "Redirecting…" : isConfigured ? "Re-install on Shopify" : "Connect Shopify"}
            </button>
          )}
          {isConfigured && (
            <button
              onClick={async () => {
                setBusy("test");
                const t = await fetchLocations();
                if (t?.ok) {
                  toast.success(`Connected to ${t.shop_name} · ${(t.locations || []).length} location(s)`);
                  (t.warnings || []).forEach((w) => toast.error(w, { duration: 8000 }));
                  if (!settings.default_location_id && (t.locations || []).length > 0) {
                    const primary = (t.locations || []).find((l) => l.primary) || (t.locations || [])[0];
                    if (primary) {
                      await api.put("/shopify/settings", { default_location_id: primary.id });
                      await load();
                    }
                  }
                } else {
                  toast.error(`Test failed: ${t?.error || "unknown"}`);
                }
                setBusy(null);
              }}
              disabled={busy === "test"}
              className="brutal-btn brutal-btn-secondary"
              data-testid="shopify-test-btn"
            >
              {busy === "test" ? "Testing…" : "Test connection"}
            </button>
          )}
          {isConfigured && (
            <button
              onClick={()=>runSync(1)}
              disabled={busy === "sync-test" || !hasLocation}
              className="brutal-btn brutal-btn-secondary"
              title={!hasLocation ? "Pick a location first" : "Push only the first product as a smoke test"}
              data-testid="shopify-sync-test-btn"
            >
              {busy === "sync-test" ? "Testing…" : "Test sync (1 product)"}
            </button>
          )}
          {isConfigured && (
            <button
              onClick={async () => {
                if (!window.confirm("Reset Shopify mapping?\n\nUse this AFTER you've deleted products in Shopify, so the next sync creates them fresh instead of trying to update the (now gone) Shopify products. Local stock/prices are untouched.")) return;
                setBusy("reset-map");
                try {
                  const { data } = await api.post("/shopify/reset-mapping");
                  toast.success(`Cleared mapping for ${data.cleared} products. Run Sync all to re-create them in Shopify.`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't reset mapping");
                } finally { setBusy(null); }
              }}
              disabled={busy === "reset-map"}
              className="brutal-btn brutal-btn-secondary"
              title="Use this after deleting products in Shopify to re-create them on next sync"
              data-testid="shopify-reset-mapping-btn"
            >
              {busy === "reset-map" ? "Resetting…" : "Reset mapping"}
            </button>
          )}
          {isConfigured && (
            <button
              onClick={async () => {
                if (!window.confirm("Disconnect Shopify? You'll need to click Connect Shopify again to reinstall.")) return;
                setBusy("disconnect");
                try {
                  // Clear the stored token by saving an empty admin_token.
                  await api.put("/shopify/settings", { admin_token: "" });
                  await load();
                  setLocations([]);
                  toast.success("Disconnected from Shopify");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't disconnect");
                } finally { setBusy(null); }
              }}
              disabled={busy === "disconnect"}
              className="brutal-btn brutal-btn-secondary !border-[#C53030] !text-[#C53030]"
              data-testid="shopify-disconnect-btn"
            >
              {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
          {isConfigured && (
            <button
              onClick={()=>runSync(null)}
              disabled={busy === "sync" || !hasLocation}
              className="brutal-btn"
              data-testid="shopify-sync-btn"
            >
              {busy === "sync" ? "Syncing…" : "Sync all"}
            </button>
          )}
          {isConfigured && (lastResult?.status === "interrupted" ||
            (lastResult?.status === "done" && (lastResult?.errors || []).length > 0)) && (
            <button
              onClick={()=>runSync(null, { skipSynced: true })}
              disabled={busy === "sync-resume" || !hasLocation}
              className="brutal-btn !bg-[#FFB100]"
              title="Push only products that haven't been synced yet"
              data-testid="shopify-resume-sync-btn"
            >
              {busy === "sync-resume" ? "Resuming…" : "Resume sync"}
            </button>
          )}
        </div>
      )}

      {lastResult && (
        <div className="brutal-card !shadow-none border-2 border-black p-3 bg-[#F4F2EC] font-mono text-xs space-y-1" data-testid="shopify-last-result">
          <div className="font-bold uppercase tracking-widest text-[10px]">
            Last result {lastResult.dry_run ? "(dry-run)" : ""}{lastResult.limit_applied ? ` · capped at ${lastResult.limit_applied}` : ""}{lastResult.skip_synced ? " · resume mode" : ""}
            {lastResult.status === "running" && (
              <span className="ml-2 !bg-[#FFB100] !text-black px-1.5 rounded">RUNNING…</span>
            )}
            {lastResult.status === "interrupted" && (
              <span className="ml-2 !bg-[#C53030] !text-white px-1.5 rounded">INTERRUPTED</span>
            )}
          </div>
          {(lastResult.status === "running" || lastResult.status === "interrupted") && (
            <div className="font-mono text-[11px]">
              {lastResult.products_processed || 0} / {lastResult.products_seen || 0} processed
            </div>
          )}
          <div>Products seen: {lastResult.products_seen}</div>
          <div>Created: {lastResult.products_created} · Updated: {lastResult.products_updated}</div>
          <div>Variants: +{lastResult.variants_created} created, ~{lastResult.variants_updated} updated</div>
          <div>Inventory levels set: {lastResult.inventory_levels_set || 0}</div>
          {(lastResult.collections_created || 0) > 0 || (lastResult.collects_added || 0) > 0 ? (
            <div data-testid="shopify-collections-row">
              Collections: +{lastResult.collections_created || 0} created · {lastResult.collects_added || 0} product-to-collection links added
            </div>
          ) : null}
          {(lastResult.warnings || []).map((w, i) => (
            <div key={i} className="text-[#C53030]">⚠ {w}</div>
          ))}
          {(lastResult.errors || []).length > 0 && (
            <div className="text-[#C53030]">
              {lastResult.errors.length} error{lastResult.errors.length > 1 ? "s" : ""}: {lastResult.errors.slice(0,3).map(e=>`${e.product}: ${e.error}`).join(" · ")}
            </div>
          )}
          {(lastResult.examples || []).length > 0 && (
            <div className="text-[#4A4A4A]">
              e.g. {lastResult.examples.map((e)=>`${e.name} (${e.variants}v, ${e.total_stock} stock)`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// =====================================================================
// Square POS Integration card — multi-tenant OAuth
// =====================================================================
function SquareIntegrationCard() {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(null);
  const { billing } = useBilling();
  const integrationsAllowed = !!billing?.limits?.integrations;
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/square/config"); setConfig(data); }
    catch { setConfig(null); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post("/square/oauth/start");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start Square install");
      setBusy(null);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Square? Future charges won't go through Square POS until you reconnect.")) return;
    setBusy("disconnect");
    try {
      await api.post("/square/oauth/disconnect");
      toast.success("Square disconnected");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't disconnect");
    } finally { setBusy(null); }
  };

  if (!config) return null;

  // Free-tier paywall
  if (!integrationsAllowed) {
    return (
      <div className="brutal-card p-5 space-y-3" data-testid="square-card-locked">
        <div className="flex items-start gap-2">
          <CreditCard size={18}/>
          <div className="flex-1">
            <div className="font-mono text-xs uppercase tracking-widest">Integration</div>
            <h3 className="font-heading text-xl font-black uppercase">Square POS · Tap to Pay</h3>
            <p className="font-mono text-xs text-[#4A4A4A] mt-1 max-w-xl">
              Charge customers right from the booth — your existing Square Reader (or Tap to Pay on your phone)
              talks to Vendor Vault. Sales auto-stamp with the Square payment id.
            </p>
          </div>
          <span className="brutal-badge !bg-[#FFB100]">Pro</span>
        </div>
        <div className="bg-[#F4F2EC] border-2 border-black p-4 font-mono text-xs flex items-center gap-3">
          <Crown size={16}/>
          <span className="flex-1">Available on Pro & Business plans.</span>
          <button
            onClick={()=>navigate("/billing#plans")}
            className="brutal-btn !py-1 !px-3 text-xs !bg-black !text-white"
            data-testid="square-upgrade-btn"
          >
            View plans <ArrowRight size={12} className="inline ml-1"/>
          </button>
        </div>
      </div>
    );
  }

  const connected = !!config.connected;

  return (
    <div className="brutal-card p-5 space-y-3" data-testid="square-card">
      <div className="flex items-start gap-2 flex-wrap">
        <CreditCard size={18}/>
        <div className="flex-1 min-w-[200px]">
          <div className="font-mono text-xs uppercase tracking-widest">Integration · {config.env}</div>
          <h3 className="font-heading text-xl font-black uppercase">Square POS</h3>
          {connected ? (
            <p className="font-mono text-xs text-[#4A4A4A] mt-1">
              Connected to merchant <span className="font-bold">{config.merchant_id || "?"}</span>
              {config.connected_at ? ` · since ${new Date(config.connected_at).toLocaleDateString()}` : null}
            </p>
          ) : (
            <p className="font-mono text-xs text-[#4A4A4A] mt-1">
              Connect your Square seller account. Charges from Vendor Vault open your Square POS app on your phone — pay with the Reader or Tap to Pay you already use.
            </p>
          )}
        </div>
        <span className={`brutal-badge ${connected ? "!bg-[#4A7C59] !text-white" : "!bg-[#4A4A4A] !text-white"}`} data-testid="square-status-badge">
          {connected ? "Connected" : "Not connected"}
        </span>
      </div>

      {!config.oauth_available && (
        <div className="alert-bar !bg-[#FFE08A]">
          <Crown size={14}/>
          <span>Square OAuth isn't configured on this server yet. Vendor Vault admins: set <code>SQUARE_APPLICATION_SECRET</code> in the backend env.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!connected ? (
          <button
            onClick={connect}
            disabled={!config.oauth_available || busy === "connect"}
            className="brutal-btn"
            data-testid="square-connect-btn"
          >
            {busy === "connect" ? "Redirecting…" : "Connect Square"}
          </button>
        ) : (
          <>
            <button
              onClick={disconnect}
              disabled={busy === "disconnect"}
              className="brutal-btn brutal-btn-secondary"
              data-testid="square-disconnect-btn"
            >
              {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
            </button>
            <button
              onClick={connect}
              disabled={!config.oauth_available || busy === "connect"}
              className="brutal-btn brutal-btn-secondary"
              data-testid="square-reconnect-btn"
            >
              Reconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
}
